import React, { useState, useEffect, useContext, useRef } from 'react';
import { getAssetUrl } from '../../api/config';
import useRecorder from "../../hooks/useRecorder";
import Header from "../../components/Header";
import StarBackground from "../../components/StarBackground";
import Timeline from "../../components/Timeline";
import ExeCore from "../../components/ExeCore";
import Controls from "../../components/Controls";
import Planet from "../../components/Planet";
import Character from "../../components/Character";
import config from './syllablesConfig';
import { AppContext } from '../../contexts/AppContext';
import '../../styles/Intro.css';

export default function Syllables({ level, onValidate }) {
    const { enableAudio, enableText } = useContext(AppContext);
    const [gameState, setGameState] = useState('PRE_STORY'); // PRE_STORY, EXERCISE, POST_STORY, FINAL
    const [storyStep, setStoryStep] = useState(0);
    
    const storyline = config.storyline[level];
    const { startRecording, stopRecording, isRecording } = useRecorder();

    // Reset dello stato quando cambia il livello
    useEffect(() => {
        setGameState('PRE_STORY');
        setStoryStep(0);
    }, [level]);

    // Use a ref for handleNextStoryStep to ensure audio callbacks use the latest version
    const handleNextStoryStepRef = useRef();
    
    const handleNextStoryStep = () => {
        if (gameState === 'PRE_STORY') {
            if (storyStep < storyline.pre.length - 1) {
                setStoryStep(prev => prev + 1);
            } else {
                setGameState('EXERCISE');
            }
        } else if (gameState === 'POST_STORY') {
            if (level === config.totalLevels) {
                setGameState('FINAL');
            } else {
                onValidate(); // Passa al prossimo livello/reward
            }
        } else if (gameState === 'FINAL') {
            onValidate();
        }
    };

    useEffect(() => {
        handleNextStoryStepRef.current = handleNextStoryStep;
    });

    // Gestione audio per la storyline
    useEffect(() => {
        let audio = null;
        if (enableAudio) {
            if (gameState === 'PRE_STORY' && storyline.pre[storyStep]) {
                const currentMsg = storyline.pre[storyStep];
                audio = new Audio(getAssetUrl(currentMsg.audio));
                if (currentMsg.autoNext) {
                    audio.onended = () => handleNextStoryStepRef.current();
                }
                audio.play().catch(console.error);
            } else if (gameState === 'POST_STORY') {
                audio = new Audio(getAssetUrl(storyline.post.audio));
                if (storyline.post.autoNext) {
                    audio.onended = () => handleNextStoryStepRef.current();
                }
                audio.play().catch(console.error);
            } else if (gameState === 'FINAL') {
                audio = new Audio(getAssetUrl('/audios/storyline/it/mars33.wav'));
                audio.play().then(() => {
                    audio.onended = () => {
                        const finalAudio = new Audio(getAssetUrl('/audios/storyline/it/mars34.wav'));
                        finalAudio.play();
                    };
                }).catch(console.error);
            }
        }
        return () => {
            if (audio) {
                audio.pause();
                audio.currentTime = 0;
                audio.onended = null;
            }
        };
    }, [gameState, storyStep, level, enableAudio]);

    return (
        <div style={{ height: '100vh', overflow: 'hidden', position: 'relative' }}>
            <Header color={config.headerColor} />
            <StarBackground imagePath={config.planetBackground} />
            <Timeline current={level} total={config.totalLevels} />
            
            {/*<Planet image={config.planetImage} />
            */}
            
            {/* Personaggi */}
            <Character
                name="Mars"
                position="left"
                isActive={
                    (gameState === 'PRE_STORY' && storyline.pre[storyStep].name === 'Mars') ||
                    (gameState === 'POST_STORY' && storyline.post.name === 'Mars') ||
                    (gameState === 'FINAL')
                }
            />
            <Character
                name="Dudi"
                position="right"
                isActive={
                    (gameState === 'PRE_STORY' && storyline.pre[storyStep].name === 'Dudi') ||
                    (gameState === 'POST_STORY' && storyline.post.name === 'Dudi')
                }
            />

            {/* Visualizzazione Storyline (Dialoghi) */}
            {(gameState === 'PRE_STORY' || gameState === 'POST_STORY' || gameState === 'FINAL') && (
                <div style={styles.storyOverlay}>
                    {enableText && (
                        <div className={`bubble ${
                            (gameState === 'PRE_STORY' && storyline.pre[storyStep].name === 'Dudi') ||
                            (gameState === 'POST_STORY' && storyline.post.name === 'Dudi') ? 'speaker-right' : 'speaker-left'
                        }`}>
                            {gameState === 'PRE_STORY' ? storyline.pre[storyStep].text :
                             gameState === 'POST_STORY' ? storyline.post.text :
                             'Benissimo, abbiamo tutti i pezzi del razzo! Ora il mio razzo è riparato.'}
                        </div>
                    )}
                    
                    {gameState === 'POST_STORY' && storyline.post.image && (
                        <img src={storyline.post.image} alt="Premio" style={styles.rewardImage} />
                    )}

                    {gameState === 'FINAL' && (
                        <img src="/assets/images/rewards/syllables/rocket.png" alt="Razzo Riparato" style={styles.rewardImage} />
                    )}

                    <button className="nextButton" onClick={handleNextStoryStep}>
                        Avanti →
                    </button>
                </div>
            )}

            {/* Fase Esercizio Centrale */}
            {gameState === 'EXERCISE' && (
                <>
                    <div style={styles.exerciseContainer}>
                        <ExeCore image={config.marsImage} size={config.marsSize} />
                    </div>
                    <Controls
                        colors={config.controlColors}
                        onStartRecording={startRecording}
                        onStopRecording={stopRecording}
                        onValidate={() => setGameState('POST_STORY')}
                        recording={isRecording}
                        context={level.toString()}
                    />
                </>
            )}
        </div>
    );
}

const styles = {
    storyOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        backgroundColor: 'rgba(0,0,0,0.5)'
    },
    exerciseContainer: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        borderRadius: '20px',
        padding: '20px',
        backdropFilter: 'blur(10px)',
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
        zIndex: 5
    },
    rewardImage: {
        width: '400px',
        margin: '20px 0',
        animation: 'float 2s infinite ease-in-out'
    }
};
